import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import useSnackbar from '@/composables/useSnackbar';
import { useI18n } from 'vue-i18n';
import axiosInstance from '@/plugins/axios';
import type {
  IMailboxAllResultsDeleteMassActionRequest,
  IMailboxAllResultsMarkAsMassActionRequest,
  IMailboxAllResultsMoveToMassActionRequest,
  IMailboxAllResultsMoveToSpecificFolderMassActionRequest,
  IMailboxArchiveMassActionResponse,
  IMailboxDeleteMassActionRequest,
  IMailboxMarkAsMassActionRequest,
  IMailboxMoveToMassActionRequest,
  IMailboxMoveToSpecificFolderMassActionRequest,
  IMassActionMarkAsActions,
  IMassActionMoveToProgressData,
  IMassActionMoveToSpecificFolderActions,
  IMassActionProgressData,
  IMassActionRequest,
  ISearchAllResultsDeleteMassActionRequest,
  ISearchAllResultsMarkAsMassActionRequest,
  ISearchAllResultsMoveToMassActionRequest,
  ISearchAllResultsMoveToSpecificFolderMassActionRequest
} from '@/types/services/massAction.service';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import type { DefaultError } from '@tanstack/query-core';
import { isAxiosError } from 'axios';
import type { IApiErrorResponse } from '@/types/axiosApi';
import { useEventBus } from '@vueuse/core';
import {
  isISearchAllResultsSelection,
  MASS_ACTION_FAILED_EVENT,
  MASS_ACTION_POST_QUEUE_EVENT,
  MASS_ACTION_PRE_QUEUE_EVENT
} from '@/types/components/mailbox-shared/mass-actions/shared.consts';
import useMailboxSharedProvides from '@/composables/mailbox/useMailboxSharedProvides';
import useOptimisticMassAction from '@/composables/mailbox/useOptimisticMassAction';
import {
  resetStaleProgressorTimer,
  clearStaleProgressorTimer
} from '@/helpers/massActionProgressTimer';
import { invalidateMailboxAndSearchListQueries } from '@/helpers/mailbox';
import { nextTick } from 'vue';

/**
 * This service composable is used for mass actions
 */
const useMassActionService = () => {
  const { userIndex } = useAuthenticatedQuery();
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();
  const { t } = useI18n();

  const { injectMailboxFlatFolders } = useMailboxSharedProvides();
  const { flatFolders } = injectMailboxFlatFolders();
  const preMassActionQueuedBus = useEventBus(MASS_ACTION_PRE_QUEUE_EVENT);
  const postMassActionQueuedBus = useEventBus(MASS_ACTION_POST_QUEUE_EVENT);
  const massActionFailedBus = useEventBus(MASS_ACTION_FAILED_EVENT);
  const { applyOptimisticUpdate } = useOptimisticMassAction();

  const massActionApiRequest = async <T = unknown>(
    postData: IMassActionRequest,
    shouldReset: boolean
  ) => {
    // Handle search all results mass action first because it is a special case where a separate
    // request is done.

    if (isISearchAllResultsSelection(postData)) {
      preMassActionQueuedBus.emit(postData);
      const rollback = await applyOptimisticUpdate(postData);
      try {
        await nextTick(); // Ensure the event is processed before the request
        const res = await axiosInstance.post<T>(
          `/u/${userIndex.value}/search/massAction`,
          postData,
          { timeout: 0 }
        );
        postMassActionQueuedBus.emit({ ...postData, shouldReset });
        return res;
      } catch (error) {
        rollback();
        massActionFailedBus.emit(postData);
        throw error;
      }
    }

    if (postData.messages.length === 0) {
      addSnackbar({
        message: t('massActions.errors.noMessagesSelected'),
        type: 'error'
      });
      return;
    }

    // Count messages per mailbox for progress tracking
    const mailboxMap = postData.messages.reduce<Record<string, number>>((acc, message) => {
      if (message.messageId === '*') {
        const folder = flatFolders.value?.find((folder) => folder.id === message.mailboxId);
        if (!folder) {
          // Should never get here
          console.error('Folder not found on mass action request');
          throw new Error('Folder not found on mass action request');
        }
        acc[message.mailboxId] = folder.total;
      } else if (acc[message.mailboxId] !== undefined) {
        acc[message.mailboxId]! += 1;
      } else {
        acc[message.mailboxId] = 1;
      }
      return acc;
    }, {});

    // Apply optimistic cache updates before the API call
    preMassActionQueuedBus.emit(postData);
    const rollback = await applyOptimisticUpdate(postData);

    Object.entries(mailboxMap).forEach(([mailboxId, count]) => {
      queryClient.setQueryData<IMassActionProgressData>(
        ['authenticatedQuery', userIndex, 'massAction', 'progress', mailboxId],
        () => {
          if (postData.action === 'moveTo') {
            return {
              handledUidSet: new Set(),
              action: postData.action,
              messages: postData.messages.map((message) => ({
                mailboxId: message.mailboxId,
                messageId: message.messageId
              })),
              total: count,
              moveToTarget: postData.moveToTarget
            } satisfies IMassActionMoveToProgressData;
          }
          return {
            handledUidSet: new Set(),
            action: postData.action,
            messages: postData.messages.map((message) => ({
              mailboxId: message.mailboxId,
              messageId: message.messageId
            })),
            total: count
          } satisfies IMassActionProgressData;
        }
      );
      // Start stale progressor timer — will reset progress if no EventSource events arrive within 3s.
      // If it does go stale, reconcile the list once so the cache doesn't drift from the server.
      resetStaleProgressorTimer(queryClient, userIndex, mailboxId, () => {
        const currentUserIndex = userIndex.value;
        if (currentUserIndex) {
          invalidateMailboxAndSearchListQueries(queryClient, currentUserIndex, mailboxId);
        }
      });
    });

    try {
      const res = await axiosInstance.post<T>(
        `/u/${userIndex.value}/mailbox/massAction`,
        {
          ...postData,
          messages: postData.messages.map((message) => ({
            mailboxId: message.mailboxId,
            messageId: message.messageId
          }))
        },
        { timeout: 0 }
      );
      postMassActionQueuedBus.emit({ ...postData, shouldReset });
      return res;
    } catch (error) {
      // Rollback optimistic updates
      rollback();
      // Reset all the progressors just added
      Object.entries(mailboxMap).forEach(([mailboxId]) => {
        clearStaleProgressorTimer(userIndex, mailboxId);
        queryClient.setQueryData<null>(
          ['authenticatedQuery', userIndex, 'massAction', 'progress', mailboxId],
          () => {
            return null;
          }
        );
      });
      massActionFailedBus.emit(postData);
      throw error;
    }
  };

  const deleteMessages = (shouldReset = true) => {
    return useMutation<
      void,
      DefaultError,
      | Omit<IMailboxDeleteMassActionRequest, 'action'>
      | Omit<IMailboxAllResultsDeleteMassActionRequest, 'action'>
      | Omit<ISearchAllResultsDeleteMassActionRequest, 'action'>
    >({
      mutationKey: ['authenticatedQuery', userIndex, 'massAction', 'delete'],
      mutationFn: async (postData) => {
        await massActionApiRequest({ ...postData, action: 'delete' }, shouldReset);
      }
    });
  };

  const markAs = (action: IMassActionMarkAsActions, shouldReset = true) => {
    return useMutation<
      void,
      DefaultError,
      | Omit<IMailboxMarkAsMassActionRequest, 'action'>
      | Omit<IMailboxAllResultsMarkAsMassActionRequest, 'action'>
      | Omit<ISearchAllResultsMarkAsMassActionRequest, 'action'>
    >({
      mutationKey: ['authenticatedQuery', userIndex, 'massAction', 'markAs', action],
      mutationFn: async (postData) => {
        await massActionApiRequest({ ...postData, action }, shouldReset);
      }
    });
  };

  const moveTo = (shouldReset = true) => {
    return useMutation<
      void,
      DefaultError,
      | Omit<IMailboxMoveToMassActionRequest, 'action'>
      | Omit<IMailboxAllResultsMoveToMassActionRequest, 'action'>
      | Omit<ISearchAllResultsMoveToMassActionRequest, 'action'>
    >({
      mutationKey: ['authenticatedQuery', userIndex, 'massAction', 'moveTo'],
      mutationFn: async (postData) => {
        await massActionApiRequest({ ...postData, action: 'moveTo' }, shouldReset);
      }
    });
  };
  const moveToSpecific = <T extends IMassActionMoveToSpecificFolderActions>(
    specificAction: T,
    shouldReset = true
  ) => {
    return useMutation<
      // Tbh not entirely sure why the void is needed, but it works out
      (T extends 'archive' ? IMailboxArchiveMassActionResponse : void) | undefined,
      DefaultError,
      | Omit<IMailboxMoveToSpecificFolderMassActionRequest, 'action'>
      | Omit<IMailboxAllResultsMoveToSpecificFolderMassActionRequest, 'action'>
      | Omit<ISearchAllResultsMoveToSpecificFolderMassActionRequest, 'action'>
    >({
      mutationKey: ['authenticatedQuery', userIndex, 'massAction', 'moveTo', specificAction],
      mutationFn: async (postData) => {
        try {
          const res = await massActionApiRequest<
            T extends 'archive' ? IMailboxArchiveMassActionResponse : void
          >(
            {
              ...postData,
              action: specificAction
            },
            shouldReset
          );
          return res?.data;
        } catch (error) {
          // In the case of the archive action there exists a chance that the user does not have
          // the "Archive" folder set as the optional system folder, but the API will make the
          // connection. So we need to handle this case specifically.
          if (
            specificAction === 'archive' &&
            isAxiosError<IApiErrorResponse>(error) &&
            error.status === 400
          ) {
            addSnackbar({
              message: t('massActions.errors.archiveFolderNotSet'),
              type: 'attention'
            });
            await queryClient.invalidateQueries({
              queryKey: ['authenticatedQuery', userIndex, 'folders']
            });
            return;
          }
          throw error;
        }
      },
      onSuccess: () => {
        if (specificAction === 'trash') {
          addSnackbar({
            message: t('massActions.trash.success'),
            type: 'success',
            timeout: 5000
          });
        }
      }
    });
  };

  return {
    deleteMessages,
    markAs,
    moveTo,
    moveToSpecific
  };
};

export default useMassActionService;
